import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Button, Stack, CircularProgress, Alert, Chip, IconButton } from '@mui/material';
import { useDocument } from '../hooks/useFirestore';
import { db } from '../firebase';
import { updateDoc, doc } from 'firebase/firestore';
import { useEffect } from 'react';
import { MarkEmailUnread as MarkUnreadIcon } from '@mui/icons-material';

interface Feedback {
  id: string;
  userId?: string;
  userEmail?: string;
  content: string;
  screenshotUrl?: string | null;
  createdAt: Date;
  isRead?: boolean;
  readAt?: Date;
}

export default function FeedbackDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: feedback, loading, error } = useDocument<Feedback>('feedback', id || '');

  // フィードバック詳細を開いた時に自動的に既読にする
  useEffect(() => {
    if (feedback && !feedback.isRead && id) {
      const markAsRead = async () => {
        try {
          await updateDoc(doc(db, 'feedback', id), {
            isRead: true,
            readAt: new Date()
          });
        } catch (error) {
          console.error('Failed to mark feedback as read:', error);
        }
      };
      markAsRead();
    }
  }, [feedback, id]);

  const handleMarkAsUnread = async () => {
    if (!id) return;
    try {
      await updateDoc(doc(db, 'feedback', id), {
        isRead: false,
        readAt: null
      });
    } catch (error) {
      console.error('Failed to mark feedback as unread:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          フィードバックの読み込みに失敗しました: {error.message}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(-1)}>戻る</Button>
      </Box>
    );
  }

  if (!feedback) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          フィードバックが見つかりません
        </Alert>
        <Button variant="outlined" onClick={() => navigate(-1)}>戻る</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">フィードバック詳細</Typography>
          {feedback && !feedback.isRead && (
            <Chip 
              label="未読" 
              color="error" 
              size="small"
            />
          )}
          {feedback && feedback.isRead && (
            <Chip 
              label="既読" 
              color="success" 
              size="small"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {feedback && feedback.isRead && (
            <IconButton 
              onClick={handleMarkAsUnread}
              title="未読に戻す"
              color="primary"
            >
              <MarkUnreadIcon />
            </IconButton>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)}>戻る</Button>
        </Box>
      </Stack>

      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {(() => { const v: any = (feedback as any)?.createdAt; try { const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v); return d.toLocaleString(); } catch { return ''; } })()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {feedback?.userEmail || feedback?.userId || '匿名'}
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {feedback?.content}
          </Typography>
          {feedback?.screenshotUrl ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                添付スクリーンショット:
              </Typography>
              <Box
                component="img"
                src={String(feedback.screenshotUrl)}
                alt="フィードバック添付画像"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 400,
                  objectFit: 'contain',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  cursor: 'pointer'
                }}
                onClick={() => feedback.screenshotUrl && window.open(feedback.screenshotUrl, '_blank')}
              />
            </Box>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
}


